import locale from "@bonliva-crm/locale";
import { isEmpty } from "lodash";
import {
  Box,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  makeStyles,
  Typography
} from "@material-ui/core";
import { useEffect, useState } from "react";
import ReactSelect, {
  components,
  createFilter,
  GroupBase,
  OptionsOrGroups
} from "react-select5";
import { CheckboxOptionComponent } from "./components/checkbox-option";
import { ClearIndicatorComponent } from "./components/clear-indicator";
import { DropdownIndicatorComponent } from "./components/dropdown-indicator";
import { GroupHeadingComponent } from "./components/group-heading";
import { MultiValueRemoveComponent } from "./components/multi-value-remove";
import { PlaceholderComponent } from "./components/placeholder";
import { SelectBadge } from "./components/select-badge";
import { useSelectStyle } from "./hooks/use-select-style";
import { MultiSelectCommonProps } from "./utils/multi-select-common-props";

export function MultiGroupedSelect<
  Option,
  Group extends GroupBase<Option> = GroupBase<Option>
>({
  optionType = "checkbox",
  showOptionDivider = true,
  ...mainProps
}: MultiSelectCommonProps<Option, Group>) {
  const { selectStyle, selectTheme } = useSelectStyle<Option, true, Group>(
    mainProps.styleProps
  );
  const classes = useStyles();

  const {
    inputLabel,
    showBadge,
    options: _options,
    isClearable,
    onClear,
    name,
    selectRef,
    ...rest
  } = mainProps;

  const [_selectedAmount, _setSelectedAmount] = useState(0);
  const selectedAmount =
    mainProps.value && Array.isArray(mainProps.value)
      ? mainProps.value.length
      : _selectedAmount;

  const [options, setOptions] = useState<OptionsOrGroups<Option, Group> | undefined>(
    _options
  );
  const [isOpen, _setIsOpen] = useState(mainProps.menuIsOpen ?? false);

  useEffect(() => {
    if (mainProps.sortOptionsOnMenuOpenFn) {
      mainProps.options &&
        setOptions(mainProps.sortOptionsOnMenuOpenFn(mainProps.options.slice()));
    } else {
      setOptions(mainProps.options);
    }
  }, [mainProps.options]);

  const hasValue =
    mainProps.value && Array.isArray(mainProps.value) && mainProps.value.length > 0;

  const firstValue =
    !isEmpty(mainProps.value) &&
    Array.isArray(mainProps.value) &&
    mainProps.value[0] &&
    "label" in mainProps.value[0] &&
    mainProps.value[0].label;

  return (
    <Box className={classes.root}>
      {showBadge && (
        <SelectBadge
          selectedAmount={selectedAmount}
          text={firstValue ? (firstValue as string) : undefined}
        />
      )}
      <FormControl>
        {inputLabel && (
          <InputLabel
            htmlFor={name}
            shrink={!!hasValue || isOpen || !!mainProps.placeholder}
          >
            <Typography
              variant="body1"
              style={{
                opacity: mainProps.isDisabled ? 0.5 : 1,
                marginBottom: "2px"
              }}
            >
              {inputLabel}
            </Typography>
          </InputLabel>
        )}

        <ReactSelect<Option, true, Group>
          components={{
            Option: (p) =>
              optionType === "checkbox" ? (
                CheckboxOptionComponent({
                  ...p,
                  showDivider: showOptionDivider,
                  onEdit: mainProps.onOptionEdit,
                  onDelete: mainProps.onOptionDelete
                })
              ) : (
                <components.Option {...p} />
              ),
            GroupHeading: (p) => GroupHeadingComponent({ ...p, textVariant: "h5" }),
            Placeholder: (p) =>
              PlaceholderComponent({
                ...p,
                hidePlaceholderOnFocus: mainProps.hidePlaceholderOnFocus
              }),
            DropdownIndicator: (p) =>
              mainProps.showDropDownIndicator ? DropdownIndicatorComponent(p) : null,
            IndicatorSeparator: null,
            ClearIndicator: (p) =>
              isClearable && onClear ? (
                ClearIndicatorComponent({ ...p, onClear: onClear })
              ) : (
                <></>
              ),
            MultiValueRemove: (p) => MultiValueRemoveComponent(p),
            MultiValue: (p) => {
              return (
                <Chip
                  size="small"
                  label={(p.data as any).label}
                  onMouseDown={(e) => {
                    e.preventDefault();
                  }}
                  onDelete={() => {
                    mainProps.onChange?.(
                      (mainProps.value as Option[]).filter(
                        (v) => (v as any).value !== (p.data as any).value
                      ),
                      { action: "remove-value", removedValue: p.data }
                    );
                    showBadge &&
                      _setSelectedAmount((mainProps.value as Option[]).length - 1);
                  }}
                />
              );
            }
          }}
          styles={selectStyle}
          theme={(theme) => ({
            ...theme,
            ...selectTheme
          })}
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          backspaceRemovesValue={false}
          noOptionsMessage={() => locale.NoMoreSearchResults}
          loadingMessage={() => <CircularProgress size={40} />}
          options={options}
          onMenuClose={() => {
            _setIsOpen(false);
          }}
          onMenuOpen={() => {
            _setIsOpen(true);
            if (mainProps.sortOptionsOnMenuOpenFn) {
              options && setOptions(mainProps.sortOptionsOnMenuOpenFn(options.slice()));
            }
          }}
          ref={selectRef}
          filterOption={createFilter({ ignoreAccents: false })}
          {...rest}
        />
      </FormControl>
    </Box>
  );
}

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    rowGap: "0.5rem",
    position: "relative",
    height: "100%"
  }
}));

export default MultiGroupedSelect;
